import React from "react";
import styled, { keyframes } from "styled-components";
import { useTranslation } from "react-i18next";

const fadeUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const HeroSection = styled.section`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 90vh;
    text-align: center;
    background: url("/images/hero.png") center/cover no-repeat;
    overflow: hidden;

    &::after {
        content: "";
        position: absolute;
        inset: 0;
        background: linear-gradient(
                180deg,
                rgba(0, 0, 0, 0.55) 0%,
                rgba(0, 0, 0, 0.75) 100%
        );
        z-index: 1;
    }
`;

const Content = styled.div`
    position: relative;
    z-index: 2;
    color: #fff;
    padding: 0 1rem;
    max-width: 900px;
    animation: ${fadeUp} 1s ease forwards;
`;

const Title = styled.h1`
    font-size: 3rem;
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    background: linear-gradient(90deg, #00c8ff, #ffb400);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;

    @media (max-width: 768px) {
        font-size: 2.2rem;
    }
`;

const Subtitle = styled.h3`
    font-weight: 400;
    margin-bottom: 1rem;
    color: #f0f0f0;
    animation: ${fadeUp} 1.4s ease forwards;
`;

const Tagline = styled.p`
    font-size: 1.1rem;
    max-width: 650px;
    margin: 0 auto 2rem;
    color: #ddd;
    animation: ${fadeUp} 1.8s ease forwards;
`;

const Buttons = styled.div`
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    justify-content: center;
    animation: ${fadeUp} 2s ease forwards;
`;

const Button = styled.a<{ secondary?: boolean }>`
  background: ${(props) => (props.secondary ? "transparent" : "#ffb400")};
  color: ${(props) => (props.secondary ? "#fff" : "#000")};
  padding: 12px 28px;
  border-radius: 30px;
  border: ${(props) => (props.secondary ? "2px solid #00c8ff" : "none")};
  font-weight: bold;
  text-decoration: none;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    background: ${(props) => (props.secondary ? "#00c8ff" : "#ffc733")};
    color: ${(props) => (props.secondary ? "#fff" : "#000")};
  }
`;

const FeatureBar = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 3rem;
  flex-wrap: wrap;
  justify-content: center;
  color: #ffeb7f;
  font-size: 0.95rem;
  animation: ${fadeUp} 2.3s ease forwards;
`;

export const Hero: React.FC = () => {
    const { t } = useTranslation();

    return (
        <HeroSection>
            <Content>
                <Title>
                    {t("hero.title", { defaultValue: "Train in Buenos Aires" })}
                </Title>

                <Subtitle>
                    {t("hero.subtitle", {
                        defaultValue: "Personal Training & Fitness Coaching",
                    })}
                </Subtitle>

                <Tagline>
                    {t("hero.tagline", {
                        defaultValue:
                            "Transform your body and mind with customized workouts, outdoor training, and expert guidance.",
                    })}
                </Tagline>

                <Buttons>
                    <Button href="#contact">
                        {t("hero.button1", { defaultValue: "Start Now" })}
                    </Button>
                    <Button href="#services" secondary>
                        {t("hero.button2", { defaultValue: "See Programs" })}
                    </Button>
                </Buttons>

                <FeatureBar>
                    <span>{t("hero.feature1", { defaultValue: "Group Training" })}</span>
                    <span>•</span>
                    <span>{t("hero.feature2", { defaultValue: "Online Coaching" })}</span>
                    <span>•</span>
                    <span>{t("hero.feature3", { defaultValue: "Nutrition Advice" })}</span>
                </FeatureBar>
            </Content>
        </HeroSection>
    );
};
